

import React, { useState, useEffect } from 'react';
// Corrected import path for types.ts
import { Article, Sector, ArticleStatus, User, UserRole } from '@/types'; // Corrected import path
import { FolderOpen, TrendingUp, HardHat, Monitor, Users, PieChart, ChevronRight, Search, Calendar, ArrowLeft, LayoutGrid, Clock, Sparkles, Lock, ChevronsRight, FileText, Plus, GripVertical } from 'lucide-react'; // Importar GripVertical
import { updateArticleSector } from '@/services/database'; // Importar a nova função

interface DashboardProps {
  currentUser: User;
  articles: Article[];
  sectors: Sector[];
  onNavigate: (view: any) => void;
  onViewArticle: (article: Article) => void;
  selectedSector: Sector | null; // Agora representa o setor/sub-setor atual na navegação
  onSelectSector: (sector: Sector | null) => void;
  onOpenCreateSectorModal: (parentId: string | null) => void; // Nova prop
}

export const Dashboard: React.FC<DashboardProps> = ({ 
  currentUser,
  articles, 
  sectors, 
  onNavigate, 
  onViewArticle,
  selectedSector, // Agora controla a "pasta" atual
  onSelectSector, // Função para navegar para um setor/sub-setor
  onOpenCreateSectorModal // Nova prop para abrir o modal de criação de setor/pasta
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [greeting, setGreeting] = useState('');

  // Estados para Drag and Drop
  const [draggingArticleId, setDraggingArticleId] = useState<string | null>(null);
  const [dragOverSectorId, setDragOverSectorId] = useState<string | null>(null);

  useEffect(() => {
    const hour = new Date().getHours();
    if (hour < 12) setGreeting('Bom dia');
    else if (hour < 18) setGreeting('Boa tarde');
    else setGreeting('Boa noite');
  }, []);

  const approvedArticles = articles.filter(a => a.status === ArticleStatus.APPROVED);
  
  // Get first name for display
  const firstName = currentUser.fullName.split(' ')[0];
  const isViewer = currentUser.role === UserRole.VIEWER;
  const canPublish = currentUser.role === UserRole.PUBLISHER || currentUser.role === UserRole.ADMIN;


  // --- Funções Auxiliares para Navegação Hierárquica ---
  const getSubSectors = (parentId: string | null) => {
    return sectors.filter(s => s.parentId === parentId);
  };

  /**
   * Helper para obter todos os IDs do setor atual e de seus setores ancestrais.
   * Usado para:
   * 1. Definir o caminho (breadcrumbs) de um setor.
   * 2. Determinar se um setor de nível superior 'contém' um artigo.
   */
  const getSectorAndAncestorIds = (currentSectorId: string | null): string[] => {
    if (!currentSectorId) return [];

    const ids: string[] = [currentSectorId];
    let currentSector: Sector | undefined = sectors.find(s => s.id === currentSectorId);
    
    while (currentSector) { // Primeiro, verifica se 'currentSector' é definido
      const parentId = currentSector.parentId; // Extrai parentId para uma variável local
      if (parentId) { // Agora, verifica se 'parentId' existe e é uma string
        ids.push(parentId);
        currentSector = sectors.find(s => s.id === parentId); // Usa o parentId extraído
      } else {
        // Se não há parentId, chegamos ao topo da hierarquia
        break;
      }
    }
    return ids;
  };

  /**
   * Helper para obter todos os IDs do setor atual e de seus setores descendentes (filhos, netos, etc.).
   * Usado para:
   * 1. Contar artigos em um setor e toda a sua hierarquia de sub-pastas para o card de "docs".
   * 2. Contar artigos em sub-pastas dentro da `renderFolderContent`.
   */
  const getSectorAndDescendantIds = (startSectorId: string | null): string[] => {
    if (!startSectorId) return [];

    const descendantIds: string[] = [startSectorId];
    let queue: string[] = [startSectorId]; // Usamos uma fila para BFS (busca em largura)

    let head = 0;
    while (head < queue.length) {
      const currentParentId = queue[head++]; // Pega o próximo item da fila e avança o head
      const children = sectors.filter(s => s.parentId === currentParentId);
      for (const child of children) {
        descendantIds.push(child.id);
        queue.push(child.id); // Adiciona os filhos à fila para processar seus próprios filhos
      }
    }
    return descendantIds;
  };

  /**
   * Helper para obter a lista de setores que formam o caminho até o setor atual (breadcrumbs).
   */
  const getSectorPath = (sector: Sector | null): Sector[] => {
    const path: Sector[] = [];
    let current: Sector | null = sector;
    while (current) {
      path.unshift(current);
      const parentIdOfCurrent = current.parentId;
      if (parentIdOfCurrent) {
        current = sectors.find(s => s.id === parentIdOfCurrent) || null;
      } else {
        current = null;
      }
    }
    return path;
  };

  // --- Funções para Cor e Ícone do Setor ---
  const getIconForSector = (name: string) => {
    switch (name.toLowerCase()) {
      case 'cmi': return <FolderOpen className="w-8 h-8 text-slate-700" />;
      case 'comercial': return <TrendingUp className="w-8 h-8 text-yellow-600" />;
      case 'facilities': return <HardHat className="w-8 h-8 text-cyan-600" />;
      case 'ti': return <Monitor className="w-8 h-8 text-blue-600" />;
      case 'rh': return <Users className="w-8 h-8 text-pink-600" />;
      case 'financeiro': return <PieChart className="w-8 h-8 text-green-600" />;
      default: return <FolderOpen className="w-8 h-8 text-slate-500" />;
    }
  };

  const getSectorColor = (name: string) => {
    switch (name.toLowerCase()) {
        case 'cmi': return 'bg-slate-50 border-slate-200 hover:border-slate-300';
        case 'comercial': return 'bg-yellow-50 border-yellow-100 hover:border-yellow-200';
        case 'facilities': return 'bg-cyan-50 border-cyan-100 hover:border-cyan-200';
        case 'ti': return 'bg-blue-50 border-blue-100 hover:border-blue-200';
        case 'rh': return 'bg-pink-50 border-pink-100 hover:border-pink-200';
        case 'financeiro': return 'bg-green-50 border-green-100 hover:border-green-200';
        default: return 'bg-white border-slate-200 hover:border-blue-300';
      }
  };

  // --- Funções de Drag and Drop ---
  const handleArticleDragStart = (e: React.DragEvent, articleId: string) => {
    if (!canPublish) { // Somente publishers e admins podem arrastar
        e.preventDefault();
        return;
    }
    e.dataTransfer.setData('articleId', articleId);
    setDraggingArticleId(articleId);
    e.currentTarget.classList.add('opacity-50', 'border-dashed', 'border-blue-300'); // Feedback visual ao arrastar
  };

  const handleArticleDragEnd = (e: React.DragEvent) => {
    setDraggingArticleId(null);
    e.currentTarget.classList.remove('opacity-50', 'border-dashed', 'border-blue-300');
    setDragOverSectorId(null); // Limpa qualquer estado de drag-over
  };

  const handleSectorDragOver = (e: React.DragEvent) => {
    e.preventDefault(); // Necessário para permitir o drop
  };

  const handleSectorDragEnter = (e: React.DragEvent, sectorId: string) => {
    if (draggingArticleId) {
      setDragOverSectorId(sectorId);
    }
  };

  const handleSectorDragLeave = (e: React.DragEvent) => {
    setDragOverSectorId(null);
  };

  const handleSectorDrop = async (e: React.DragEvent, targetSector: Sector) => {
    e.preventDefault();
    setDragOverSectorId(null);

    const articleId = e.dataTransfer.getData('articleId');
    if (!articleId) return;

    const draggedArticle = approvedArticles.find(a => a.id === articleId);
    if (!draggedArticle) {
        console.error("Artigo arrastado não encontrado.");
        return;
    }

    // Validação de permissões
    if (!canPublish) {
        alert("Você não tem permissão para mover artigos.");
        return;
    }

    // Prevenir soltar em si mesmo
    if (draggedArticle.sectorId === targetSector.id) {
        console.log("Artigo solto no mesmo setor. Nenhuma alteração necessária.");
        return;
    }

    if (!window.confirm(`Tem certeza que deseja mover "${draggedArticle.title}" para "${targetSector.name}"?`)) {
        return;
    }

    try {
      await updateArticleSector(articleId, targetSector.id, targetSector.name);
      // O UI se atualizará automaticamente devido à inscrição do Firestore
      console.log(`Artigo "${draggedArticle.title}" movido para "${targetSector.name}"`);
    } catch (error) {
      console.error("Erro ao mover artigo:", error);
      alert("Erro ao mover artigo. Tente novamente.");
    } finally {
      setDraggingArticleId(null); // Garante que o estado de arraste seja limpo
    }
  };


  // --- Render Functions ---

  const renderWelcomeHeader = () => (
    <div className="mb-10 animate-fade-in">
       <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-6">
         <div>
            <p className="text-slate-500 text-sm font-medium mb-1 flex items-center gap-2">
               <Calendar className="w-4 h-4" /> {new Date().toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long' })}
            </p>
            <h1 className="text-3xl font-bold text-slate-900">
               {greeting}, {firstName}.
            </h1>
         </div>
         <div className="hidden md:block">
            <button 
              onClick={() => onNavigate('chat')}
              className="flex items-center gap-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-5 py-2.5 rounded-xl font-medium shadow-md hover:shadow-lg transition-all hover:-translate-y-0.5"
            >
               <Sparkles className="w-4 h-4" /> Perguntar à IA
            </button>
         </div>
       </div>

       {/* Quick Stats Cards */}
       <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white p-4 rounded-xl border border-slate-100 shadow-sm flex flex-col justify-between h-24">
             <div className="flex justify-between items-start">
                <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Artigos Ativos</span>
                <FolderOpen className="w-4 h-4 text-blue-500" />
             </div>
             <p className="text-2xl font-bold text-slate-800">{isViewer ? '-' : approvedArticles.length}</p>
          </div>
          
          <div className="bg-white p-4 rounded-xl border border-slate-100 shadow-sm flex flex-col justify-between h-24">
             <div className="flex justify-between items-start">
                <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Setores</span>
                <LayoutGrid className="w-4 h-4 text-purple-500" />
             </div>
             <p className="text-2xl font-bold text-slate-800">{isViewer ? '-' : sectors.length}</p>
          </div>

          <div className="bg-white p-4 rounded-xl border border-slate-100 shadow-sm flex flex-col justify-between h-24">
             <div className="flex justify-between items-start">
                <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Atualizados (30d)</span>
                <Clock className="w-4 h-4 text-green-500" />
             </div>
             <p className="2xl font-bold text-slate-800">
                {isViewer ? '-' : approvedArticles.filter(a => new Date(a.updatedAt) > new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)).length}
             </p>
          </div>

          <div className="bg-white p-4 rounded-xl border border-slate-100 shadow-sm flex flex-col justify-between h-24">
             <div className="flex justify-between items-start">
                <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Status do Sistema</span>
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
             </div>
             <p className="text-sm font-bold text-green-700 bg-green-50 inline-block px-2 py-1 rounded w-max mt-auto">
                Operacional
             </p>
          </div>
       </div>
    </div>
  );

  const renderSearchBar = () => (
    <div className="relative w-full max-w-full mx-auto mb-8 animate-fade-in delay-100">
        <div className="relative group">
            <Search className="w-5 h-5 absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-blue-500 transition-colors" />
            <input 
              type="text" 
              placeholder="Pesquisar por políticas, procedimentos ou manuais..." 
              className="w-full pl-12 pr-4 py-4 text-base bg-white border border-slate-200 rounded-2xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all hover:border-blue-300"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                onSelectSector(null); // Limpa o setor selecionado ao pesquisar
              }}
            />
        </div>
    </div>
  );

  const renderSectorGrid = () => {
    // Top-level sectors (parentId is null or undefined)
    const topLevelSectors = sectors.filter(sector => 
        !sector.parentId && (approvedArticles.some(a => getSectorAndAncestorIds(a.sectorId).includes(sector.id)) || getSubSectors(sector.id).length > 0)
    );

    return (
        <div className="animate-fade-in delay-150">
            <h3 className="text-lg font-bold text-slate-800 mb-6 flex items-center gap-2">
                <LayoutGrid className="w-5 h-5 text-blue-600" />
                Base de Conhecimento por Setor
            </h3>
            
            {topLevelSectors.length === 0 ? (
                 <div className="text-center py-16 bg-white rounded-2xl border border-dashed border-slate-300 shadow-sm">
                    <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-4">
                        <FolderOpen className="w-8 h-8 text-slate-300" />
                    </div>
                    <p className="text-slate-500 font-medium">Nenhum departamento possui artigos publicados ou sub-setores ainda.</p>
                    <p className="text-sm mt-1">O conteúdo aparecerá aqui assim que for aprovado.</p>
                 </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {topLevelSectors.map(sector => {
                    // Contagem de artigos no setor e em todos os seus descendentes
                    const descendantSectorIds = getSectorAndDescendantIds(sector.id);
                    const articleCountInHierarchy = approvedArticles.filter(a => descendantSectorIds.includes(a.sectorId)).length;
                    
                    // Mostra apenas o número de artigos na hierarquia
                    const totalDocs = articleCountInHierarchy; 
                    
                    if (totalDocs === 0 && getSubSectors(sector.id).length === 0) return null; // Só renderiza se tiver artigos ou sub-setores

                    return (
                    <button 
                        key={sector.id} 
                        onClick={() => onSelectSector(sector)}
                        onDragOver={handleSectorDragOver}
                        onDragEnter={(e) => handleSectorDragEnter(e, sector.id)}
                        onDragLeave={handleSectorDragLeave}
                        onDrop={(e) => handleSectorDrop(e, sector)}
                        className={`p-6 rounded-2xl border transition-all duration-300 text-left group relative overflow-hidden shadow-sm hover:shadow-md ${getSectorColor(sector.name)} ${dragOverSectorId === sector.id ? 'is-drag-over' : ''}`}
                    >
                        <div className="flex items-start justify-between mb-4 relative z-10">
                        <div className="p-3 bg-white rounded-xl shadow-sm group-hover:scale-110 transition-transform duration-300">
                            {getIconForSector(sector.name)}
                        </div>
                        <span className="bg-white/80 backdrop-blur-sm px-3 py-1 rounded-full text-xs font-bold text-slate-600 shadow-sm">
                            {totalDocs} docs
                        </span>
                        </div>
                        <h4 className="text-xl font-bold text-slate-800 mb-1 relative z-10">{sector.name}</h4>
                        <p className="text-sm text-slate-500 relative z-10 flex items-center gap-1 group-hover:translate-x-1 transition-transform">
                        Acessar pasta <ChevronRight className="w-4 h-4" />
                        </p>
                    </button>
                    );
                })}
                </div>
            )}
        </div>
    );
  };

  const renderFolderContent = () => {
    if (!selectedSector) return null;

    const currentSubSectors = getSubSectors(selectedSector.id);
    
    // Agora, filtra para mostrar APENAS artigos que estão diretamente no selectedSector
    const articlesDirectlyInSelectedSector = approvedArticles.filter(a => a.sectorId === selectedSector.id);

    const path = getSectorPath(selectedSector);

    const handleBack = () => {
      if (selectedSector.parentId) {
        onSelectSector(sectors.find(s => s.id === selectedSector.parentId) || null);
      } else {
        onSelectSector(null); // Back to top-level grid
      }
    };

    return (
        <div className="animate-fade-in space-y-6">
            <div className="flex items-center gap-4 mb-6">
                <button 
                    onClick={handleBack}
                    className="p-3 hover:bg-white hover:shadow-md rounded-full transition-all text-slate-500 border border-transparent hover:border-slate-200"
                    title="Voltar para o nível anterior"
                >
                    <ArrowLeft className="w-6 h-6" />
                </button>
                <div>
                  <h2 className="2xl font-bold text-slate-800">{selectedSector.name}</h2>
                  <nav className="flex items-center text-xs font-black uppercase tracking-widest text-slate-400">
                    <button onClick={() => onSelectSector(null)} className="hover:text-blue-600">Base de Conhecimento</button>
                    {path.map((sec, index) => (
                      <React.Fragment key={sec.id}>
                        <ChevronRight className="w-4 h-4 mx-2 opacity-50" />
                        <button onClick={() => onSelectSector(sec)} className={`hover:text-blue-600 ${index === path.length - 1 ? 'text-blue-600' : ''}`}>
                          {sec.name}
                        </button>
                      </React.Fragment>
                    ))}
                  </nav>
                </div>
                {canPublish && (
                    <button 
                        onClick={() => onOpenCreateSectorModal(selectedSector.id)} // Abre modal para criar sub-pasta
                        className="ml-auto flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-xl text-xs font-black uppercase shadow-lg hover:bg-blue-700 transition-all"
                    >
                        <Plus size={16} /> Nova Pasta
                    </button>
                )}
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden min-h-[400px]">
                {(currentSubSectors.length === 0 && articlesDirectlyInSelectedSector.length === 0) ? (
                    <div className="flex flex-col items-center justify-center h-80 text-slate-400 bg-slate-50/50">
                        <FolderOpen className="w-16 h-16 mb-4 opacity-20" />
                        <p className="text-lg font-medium">Esta pasta está vazia.</p>
                        <p className="text-sm mt-1">Adicione sub-setores ou artigos aqui.</p>
                    </div>
                ) : (
                    <div className="divide-y divide-slate-100">
                        {/* Render Sub-Sectors as Folders */}
                        {currentSubSectors.sort((a,b) => a.name.localeCompare(b.name)).map(subSector => {
                            const articlesInSubSectorHierarchy = approvedArticles.filter(a => getSectorAndDescendantIds(subSector.id).includes(a.sectorId)).length;
                            return (
                                <button 
                                    key={subSector.id} 
                                    onClick={() => onSelectSector(subSector)}
                                    onDragOver={handleSectorDragOver}
                                    onDragEnter={(e) => handleSectorDragEnter(e, subSector.id)}
                                    onDragLeave={handleSectorDragLeave}
                                    onDrop={(e) => handleSectorDrop(e, subSector)}
                                    className={`w-full text-left p-6 hover:bg-blue-50 transition-colors cursor-pointer group flex items-center justify-between ${dragOverSectorId === subSector.id ? 'is-drag-over' : ''}`}
                                >
                                    <div className="flex items-center gap-4">
                                        <FolderOpen className="w-6 h-6 text-blue-500 group-hover:scale-105 transition-transform" />
                                        <div>
                                            <h4 className="text-lg font-bold text-slate-800 group-hover:text-blue-600 transition-colors">
                                                {subSector.name}
                                            </h4>
                                            <p className="text-sm text-slate-500">
                                                {getSubSectors(subSector.id).length} sub-pastas, {articlesInSubSectorHierarchy} artigos na hierarquia
                                            </p>
                                        </div>
                                    </div>
                                    <ChevronRight className="w-5 h-5 text-slate-300 group-hover:text-blue-500" />
                                </button>
                            );
                        })}

                        {/* Render Articles DIRECTLY in Current Sector */}
                        {articlesDirectlyInSelectedSector.sort((a,b) => a.title.localeCompare(b.title)).map(article => (
                            <div 
                                key={article.id} 
                                onClick={() => onViewArticle(article)}
                                draggable={canPublish} // Apenas arrastável se tiver permissão
                                onDragStart={(e) => handleArticleDragStart(e, article.id)}
                                onDragEnd={handleArticleDragEnd}
                                className={`p-6 hover:bg-slate-50 transition-colors cursor-pointer group flex flex-col md:flex-row gap-4 md:items-center justify-between ${draggingArticleId === article.id ? 'is-dragging' : ''}`}
                            >
                                {/* Drag Handle */}
                                {canPublish && (
                                  <div className="flex items-center justify-center pr-4 text-slate-300 group-hover:text-blue-500 cursor-grab active:cursor-grabbing">
                                      <GripVertical size={20} />
                                  </div>
                                )}
                                <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-2">
                                        <span className="text-[10px] font-bold text-blue-600 bg-blue-50 px-2 py-0.5 rounded border border-blue-100 uppercase tracking-wider">
                                            {article.sectorName}
                                        </span>
                                        {article.tags.map(tag => (
                                            <span key={tag} className="text-[10px] text-slate-500 bg-slate-100 px-2 py-0.5 rounded border border-slate-200">
                                                {tag}
                                            </span>
                                        ))}
                                    </div>
                                    <h4 className="text-lg font-bold text-slate-800 group-hover:text-blue-600 transition-colors mb-2">
                                        {article.title}
                                    </h4>
                                    <p className="text-sm text-slate-500 line-clamp-2 leading-relaxed">
                                        {article.content.replace(/[#*]/g, '').substring(0, 160)}...
                                    </p>
                                </div>

                                <div className="flex items-center gap-6 text-sm text-slate-400 md:text-right min-w-[200px] md:justify-end border-t md:border-t-0 border-slate-100 pt-4 md:pt-0 mt-2 md:mt-0">
                                    <div className="flex items-center gap-1.5" title="Autor">
                                        <div className="w-6 h-6 rounded-full bg-slate-200 overflow-hidden">
                                            <img 
                                          src={`https://ui-avatars.com/api/?name=${encodeURIComponent(article.authorName)}&background=random`} 
                                          alt="" 
                                          className="w-full h-full object-cover"
                                        />
                                    </div>
                                    <span className="truncate max-w-[100px]">{article.authorName.split(' ')[0]}</span>
                                </div>
                                <div className="flex items-center gap-1.5" title="Data de Atualização">
                                    <Calendar className="w-4 h-4" />
                                    <span>{new Date(article.updatedAt).toLocaleDateString()}</span>
                                    </div>
                                <ChevronRight className="w-5 h-5 text-slate-300 group-hover:text-blue-500 hidden md:block" />
                            </div>
                        </div>
                    ))}
                </div>
            )}
            </div>
        </div>
    );
  };


  const renderArticleSearchResults = (title: string, subtitle?: string) => (
    <div className="animate-fade-in space-y-6">
        <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
                <div>
                    <h2 className="text-2xl font-bold text-slate-800">{title}</h2>
                    {subtitle && <p className="text-slate-500">{subtitle}</p>}
                </div>
            </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden min-h-[400px]">
            {approvedArticles.filter(a => 
                a.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                a.tags.some(t => t.toLowerCase().includes(searchTerm.toLowerCase())) ||
                a.authorName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                a.content.toLowerCase().includes(searchTerm.toLowerCase())
            ).length === 0 ? (
                <div className="flex flex-col items-center justify-center h-80 text-slate-400 bg-slate-50/50">
                    <FileText className="w-16 h-16 mb-4 opacity-20" />
                    <p className="text-lg font-medium">Nenhum documento encontrado.</p>
                    {searchTerm && <p className="text-sm mt-1">Tente usar outros termos mais genéricos.</p>}
                    {searchTerm && (
                        <button 
                          onClick={() => setSearchTerm('')} 
                          className="mt-4 text-blue-600 hover:underline text-sm font-medium"
                        >
                            Limpar filtros
                        </button>
                    )}
                </div>
            ) : (
                <div className="divide-y divide-slate-100">
                    {approvedArticles.filter(a => 
                        a.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                        a.tags.some(t => t.toLowerCase().includes(searchTerm.toLowerCase())) ||
                        a.authorName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                        a.content.toLowerCase().includes(searchTerm.toLowerCase())
                    ).sort((a,b) => a.title.localeCompare(b.title)).map(article => (
                        <div 
                            key={article.id} 
                            onClick={() => onViewArticle(article)}
                            draggable={canPublish} // Apenas arrastável se tiver permissão
                            onDragStart={(e) => handleArticleDragStart(e, article.id)}
                            onDragEnd={handleArticleDragEnd}
                            className={`p-6 hover:bg-slate-50 transition-colors cursor-pointer group flex flex-col md:flex-row gap-4 md:items-center justify-between ${draggingArticleId === article.id ? 'is-dragging' : ''}`}
                        >
                            {/* Drag Handle */}
                            {canPublish && (
                              <div className="flex items-center justify-center pr-4 text-slate-300 group-hover:text-blue-500 cursor-grab active:cursor-grabbing">
                                  <GripVertical size={20} />
                              </div>
                            )}
                            <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2">
                                    <span className="text-[10px] font-bold text-blue-600 bg-blue-50 px-2 py-0.5 rounded border border-blue-100 uppercase tracking-wider">
                                        {article.sectorName}
                                    </span>
                                    {article.tags.map(tag => (
                                        <span key={tag} className="text-[10px] text-slate-500 bg-slate-100 px-2 py-0.5 rounded border border-slate-200">
                                            {tag}
                                        </span>
                                    ))}
                                </div>
                                <h4 className="text-lg font-bold text-slate-800 group-hover:text-blue-600 transition-colors mb-2">
                                    {article.title}
                                </h4>
                                <p className="text-sm text-slate-500 line-clamp-2 leading-relaxed">
                                    {article.content.replace(/[#*]/g, '').substring(0, 160)}...
                                </p>
                            </div>

                            <div className="flex items-center gap-6 text-sm text-slate-400 md:text-right min-w-[200px] md:justify-end border-t md:border-t-0 border-slate-100 pt-4 md:pt-0 mt-2 md:mt-0">
                                <div className="flex items-center gap-1.5" title="Autor">
                                    <div className="w-6 h-6 rounded-full bg-slate-200 overflow-hidden">
                                        <img 
                                          src={`https://ui-avatars.com/api/?name=${encodeURIComponent(article.authorName)}&background=random`} 
                                          alt="" 
                                          className="w-full h-full object-cover"
                                        />
                                    </div>
                                    <span className="truncate max-w-[100px]">{article.authorName.split(' ')[0]}</span>
                                </div>
                                <div className="flex items-center gap-1.5" title="Data de Atualização">
                                    <Calendar className="w-4 h-4" />
                                    <span>{new Date(article.updatedAt).toLocaleDateString()}</span>
                                    </div>
                                <ChevronRight className="w-5 h-5 text-slate-300 group-hover:text-blue-500 hidden md:block" />
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    </div>
  );


  const renderRestrictedAccess = () => (
      <div className="bg-slate-50 border border-slate-200 rounded-3xl p-12 text-center animate-fade-in flex flex-col items-center justify-center min-h-[400px]">
          <div className="w-24 h-24 bg-white rounded-full shadow-sm flex items-center justify-center mb-6">
              <Lock className="w-10 h-10 text-slate-300" />
          </div>
          <h2 className="text-2xl font-black text-slate-800 uppercase tracking-tight mb-3">Acesso ao Acervo Restrito</h2>
          <p className="text-slate-500 max-w-lg mx-auto leading-relaxed mb-8">
              Seu perfil de usuário ({currentUser.role}) não permite a navegação direta pelos documentos e pastas da Wiki. 
              Para encontrar informações, utilize nossa Inteligência Artificial.
          </p>
          <button 
              onClick={() => onNavigate('chat')}
              className="flex items-center gap-3 bg-blue-600 text-white px-8 py-4 rounded-2xl font-bold shadow-xl shadow-blue-900/10 hover:shadow-blue-900/20 hover:bg-blue-700 transition-all hover:-translate-y-1"
          >
              <Sparkles className="w-5 h-5" />
              Acessar Soes Brain (Chat IA)
          </button>
      </div>
  );

  return (
    <div className="max-w-7xl mx-auto">
      {/* Executive Header - Only show if not drilling down AND not searching */}
      {!selectedSector && !searchTerm && renderWelcomeHeader()}

      {isViewer ? (
          renderRestrictedAccess()
      ) : (
          <>
            {/* Persistent Search */}
            {renderSearchBar()}

            {/* Main Content Area */}
            {searchTerm ? (
                renderArticleSearchResults(`Resultados para "${searchTerm}"`, `${approvedArticles.filter(a => 
                    a.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    a.tags.some(t => t.toLowerCase().includes(searchTerm.toLowerCase())) ||
                    a.authorName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    a.content.toLowerCase().includes(searchTerm.toLowerCase())
                ).length} documentos encontrados`)
            ) : selectedSector ? (
                renderFolderContent() // Nova função para o conteúdo da pasta/setor
            ) : (
                renderSectorGrid() // Grade de setores de nível superior
            )}
          </>
      )}
    </div>
  );
};